import { fetchApiJson } from '@vend/utilities'
import {
  DiscoverResult,
  loadStripeTerminal,
  Terminal,
} from '@stripe/terminal-js'
import {
  INTEGRATION_TYPE,
  TERMINAL_EVENT,
  TerminalEventMonitoring,
  TerminalModel,
} from '@lightspeed-payments/payments-terminal-sdk'
import { TimerStarterFunction } from '../hooks/timeTracking'
import { isErrorResponse, onUnexpectedDisconnect } from './errorChecking'
import {
  DEFAULT_TEST_CARD,
  LSPAY_STEPS,
  PAIRING_ERROR,
  TEST_CARD_LOCAL_STORAGE_KEY,
  TRANSACTION_TYPE,
} from './lspayConstants'
import { getMonitoringInstance } from './terminalMonitoring'
import { getDeviceType } from './lspay.types'
import { trackTerminalCompleteWithError } from './terminalTracking'

interface StoreDetails {
  outletId: string
  registerId: string
  pairingSN: string
  isSimulated: boolean
  terminalID?: string
  terminalModel?: string
}

export const connectToTerminal = async (
  details: StoreDetails,
  isSandbox: boolean,
  trackTiming: TimerStarterFunction,
  isRefund = false
): Promise<{
  terminal: Terminal
  terminalMonitoringInstance: TerminalEventMonitoring
}> => {
  const {
    outletId,
    registerId,
    pairingSN,
    isSimulated,
    terminalID,
    terminalModel: deviceType,
  } = details

  const terminalModel: TerminalModel = getDeviceType(deviceType)
  const { terminal, terminalMonitoringInstance } = await initStripeTerminal(
    outletId,
    registerId,
    isSandbox
  )

  // Driven by feature flag: lspay_simulated_reader
  const config = { simulated: isSimulated }
  if (isSimulated) {
    // eslint-disable-next-line no-console
    console.info('Using the LSPAY simulator')
  }

  terminalMonitoringInstance.sendEvent(TERMINAL_EVENT.START, {
    terminal: {
      id: terminalID,
      model: terminalModel,
    },
    transactionType: isRefund
      ? TRANSACTION_TYPE.REFUND
      : TRANSACTION_TYPE.PAYMENT,
  })
  const stopDiscoverTimer = trackTiming(LSPAY_STEPS.DISCOVER)
  const response = await terminal.discoverReaders(config)
  stopDiscoverTimer()

  if (isErrorResponse(response)) {
    const { error } = response
    trackTerminalCompleteWithError(terminalMonitoringInstance, error, isRefund)
    throw error
  }

  const { discoveredReaders } = response as DiscoverResult

  const reader = discoveredReaders.find(
    reader => reader.serial_number === pairingSN
  )

  if (!reader) {
    const readerError = new Error(
      `Pairing error, could not find reader with SN ${pairingSN}`
    )

    readerError.name = PAIRING_ERROR
    trackTerminalCompleteWithError(
      terminalMonitoringInstance,
      readerError,
      isRefund
    )
    throw readerError
  }

  const stopConnectTimer = trackTiming(LSPAY_STEPS.CONNECT)
  const connectedReader = await terminal.connectReader(reader)
  stopConnectTimer()

  if (isErrorResponse(connectedReader)) {
    const { error } = connectedReader
    trackTerminalCompleteWithError(terminalMonitoringInstance, error, isRefund)
    throw error
  }

  if (isSimulated) {
    const testCardNumber = getTestCard()
    // eslint-disable-next-line no-console
    console.info(`Using the LSPAY test card: ${testCardNumber}`)
    terminal.setSimulatorConfiguration({ testCardNumber })
  }

  return { terminal, terminalMonitoringInstance }
}

const getTestCard = (): string => {
  return localStorage.getItem(TEST_CARD_LOCAL_STORAGE_KEY) || DEFAULT_TEST_CARD
}

const initStripeTerminal = async (
  outletId: string,
  registerId: string,
  isSandbox: boolean
): Promise<{
  terminal: Terminal
  terminalMonitoringInstance: TerminalEventMonitoring
}> => {
  const terminalMonitoringInstance = getMonitoringInstance(
    outletId,
    isSandbox,
    INTEGRATION_TYPE.LOCAL
  )
  const StripeTerminal = await loadStripeTerminal()
  if (!StripeTerminal) throw new Error('Could not load Stripe Terminal')

  let terminal = StripeTerminal.create({
    onFetchConnectionToken: fetchConnectionToken(outletId, registerId),
    onUnexpectedReaderDisconnect: onUnexpectedDisconnect,
    readerBehavior: {
      // Allows the cancellation of the collect payment method from the terminal.
      allowCustomerCancel: true,
    },
  })
  terminal = terminalMonitoringInstance.defineTerminal(terminal)

  return { terminal, terminalMonitoringInstance }
}

const fetchConnectionToken = (
  outletId: string,
  registerId: string
) => async () => {
  const response = await fetchApiJson(
    `/api/2.0/outlets/${outletId}/lspay/token/${registerId}?pairingData=false`
  )
  return response.token
}
